import { assetUrl, API, downloadUrl } from "@/lib/api";
import { IconDownload } from "@/components/DownloadButton";
import { Badge } from "@/components/ui/badge";
import { Copy, Mail, Hash, LayoutTemplate, Image as ImageIcon, Check, Package, FileText } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const CopyBtn = ({ text, testid }) => {
  const [done, setDone] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setDone(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setDone(false), 1500);
  };
  return (
    <button data-testid={testid} onClick={copy} className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs bg-white/5 hover:bg-[#22C55E]/20 text-[#94A3B8] hover:text-[#22C55E] transition-colors">
      {done ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {done ? "Copied" : "Copy"}
    </button>
  );
};

const Section = ({ icon: Icon, title, children }) => (
  <div className="glass rounded-xl p-5">
    <div className="flex items-center gap-2 mb-4 text-sm font-semibold">
      <Icon className="h-4 w-4 text-[#22C55E]" /> {title}
    </div>
    {children}
  </div>
);

export const CampaignHub = ({ project }) => {
  const c = project.campaign;
  if (!c)
    return project.status === "processing" ? (
      <div className="glass rounded-2xl py-20 text-center text-[#94A3B8]"><Hash className="h-8 w-8 mx-auto text-[#22C55E] animate-pulse mb-3" />Campaign is being generated… assets will appear here shortly.</div>
    ) : project.status === "error" ? (
      <div className="glass rounded-2xl py-20 text-center text-red-300">Campaign failed to generate. See the developer drawer logs.</div>
    ) : (
      <div className="glass rounded-2xl py-20 text-center text-[#94A3B8]">Campaign is not available for this project.</div>
    );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold">{c.brand_name}</h2>
          {c.tagline && <p className="text-[#22C55E] mt-1 font-serif-i text-lg">{c.tagline}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <a data-testid="download-campaign-pack-btn" href={`${API}/projects/${project.id}/campaign-pack`} download
            className={`inline-flex items-center gap-2 rounded-full bg-[#22C55E] text-[#0F1117] font-semibold text-sm px-4 py-2 hover:opacity-90 transition-opacity ${project.status === "processing" ? "pointer-events-none opacity-50" : ""}`}>
            <Package className="h-4 w-4" /> Ready-to-post pack (.zip)
          </a>
          <a data-testid="download-campaign-copy-btn" href={`${API}/projects/${project.id}/campaign-copy`} download
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 text-sm px-4 py-2 hover:bg-white/10 transition-colors">
            <FileText className="h-4 w-4" /> Copy only (.txt)
          </a>
        </div>
      </div>

      {/* Hero banner */}
      {c.banner_asset_id && (
        <div className="relative rounded-xl overflow-hidden border border-white/10">
          <img data-testid="campaign-banner-img" src={assetUrl(c.banner_asset_id)} alt="banner" className="w-full h-56 object-cover" />
          <div className="absolute top-3 right-3"><IconDownload testid="download-banner-btn" title="Download banner" href={downloadUrl(c.banner_asset_id, "hero-banner")} /></div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Social thread */}
        <Section icon={Hash} title="3-Part Social Launch Thread">
          <div className="space-y-3">
            {(c.social_thread || []).map((p, i) => (
              <div key={i} data-testid={`social-post-${i}`} className="rounded-lg bg-[#0F1117]/70 border border-white/5 p-3">
                <div className="flex items-center justify-between mb-2">
                  <Badge className="bg-[#3B82F6]/20 text-[#3B82F6] border-0 text-[10px]">Post {i + 1}</Badge>
                  <CopyBtn text={p} testid={`copy-social-${i}`} />
                </div>
                <p className="text-sm text-[#CBD5E1] whitespace-pre-wrap leading-relaxed">{p}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Email */}
        <Section icon={Mail} title="Email Campaign Copy">
          <div className="rounded-lg bg-[#0F1117]/70 border border-white/5 p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-[#94A3B8]">Subject</span>
              <CopyBtn text={`${c.email?.subject}\n\n${c.email?.body}`} testid="copy-email" />
            </div>
            <p className="text-sm font-semibold text-[#F8FAFC] mb-3">{c.email?.subject}</p>
            <p className="text-sm text-[#CBD5E1] whitespace-pre-wrap leading-relaxed">{c.email?.body}</p>
          </div>
        </Section>

        {/* Landing page */}
        <Section icon={LayoutTemplate} title="High-Converting Landing Page">
          <div className="rounded-lg bg-[#0F1117]/70 border border-white/5 p-4 text-center">
            <div className="flex justify-end mb-2"><CopyBtn text={`${c.landing?.headline}\n${c.landing?.subheadline}`} testid="copy-landing" /></div>
            <h3 className="font-display text-xl font-bold text-[#F8FAFC]">{c.landing?.headline}</h3>
            <p className="text-sm text-[#94A3B8] mt-2">{c.landing?.subheadline}</p>
            <div className="inline-block mt-4 bg-[#22C55E] text-[#0F1117] font-semibold text-sm px-5 py-2 rounded-full">{c.landing?.cta}</div>
            <ul className="mt-4 space-y-1.5 text-left">
              {(c.landing?.bullets || []).map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#CBD5E1]"><Check className="h-4 w-4 text-[#22C55E] shrink-0 mt-0.5" /> {b}</li>
              ))}
            </ul>
          </div>
        </Section>

        {/* Ad visual prompts */}
        <Section icon={ImageIcon} title="Ad Visual Prompts">
          <div className="space-y-3">
            {(c.ad_prompts || []).map((p, i) => (
              <div key={i} data-testid={`ad-prompt-${i}`} className="rounded-lg bg-[#0F1117]/70 border border-white/5 p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <Badge className="bg-[#A855F7]/20 text-[#A855F7] border-0 text-[10px]">Visual {i + 1}</Badge>
                  <CopyBtn text={p} testid={`copy-ad-${i}`} />
                </div>
                {c.ad_asset_ids?.[i] && (
                  <div className="relative mb-2">
                    <img data-testid={`ad-visual-img-${i}`} src={assetUrl(c.ad_asset_ids[i])} alt={`Ad visual ${i + 1}`} className="w-full h-36 object-cover rounded-md" />
                    <div className="absolute top-2 right-2"><IconDownload testid={`download-ad-visual-${i}`} title="Download ad visual" href={downloadUrl(c.ad_asset_ids[i], `ad-visual-${i + 1}`)} /></div>
                  </div>
                )}
                <p className="text-xs text-[#94A3B8] font-mono leading-relaxed">{p}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
};
